import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

// ---- types ----

export interface DeclarationGuidance {
  at_1100: string;
  at_1430: string;
}

export interface Advice {
  one_sentence: string;
  trading_action: string;
  trading_action_label: string;
  solar_pressure_vs_official: string;
  solar_pressure_label: string;
  risk_level: string;
  confidence: string;
  confidence_label: string;
  main_regions: string[];
  primary_reasons: string[];
  review_required: boolean;
  review_reasons: string[];
  reason_cards: {
    title: string;
    severity: string;
    summary: string;
  }[];
  declaration_guidance: DeclarationGuidance;
}

export interface Calibration {
  method: string;
  sample_days: number;
  weak_or_failed_rate: number | null;
  avg_realization_ratio: number | null;
  p80_shortfall_mw: number | null;
  status: string;
  bucket_key: string | null;
}

export interface WeatherAdjustment {
  ifs_weather_adjusted_mw: number | null;
  weather_vs_official_delta_mw: number | null;
  bulletin_adjustment: string;
  recent_realization_adjustment: string;
}

export interface CityImpact {
  region: string;
  official_allocated_mw: number | null;
  recommended_realizable_mw: number | null;
  shortfall_mw: number | null;
  weather_causes: string[];
  evidence_refs: string[];
}

export interface SolarData {
  official_midday_mw: number | null;
  recommended_discount_ratio: number | null;
  recommended_realizable_mw: number | null;
  expected_shortfall_mw: number | null;
  risk_discount_ratio: number | null;
  risk_realizable_mw: number | null;
  capacity_basis: string;
  capacity_basis_note: string;
  calibration: Calibration;
  weather_adjustment: WeatherAdjustment;
  city_impacts: CityImpact[];
}

export interface WeatherBriefSection {
  summary: string;
  level?: string;
  regions: string[];
  trading_meaning: string;
  evidence_refs: string[];
}

export interface OfficialBulletinSection {
  date: string | null;
  valid_from: string | null;
  valid_to: string | null;
  summary: string;
  risk_tags: string[];
  regions: string[];
}

export interface WeatherBrief {
  solar: WeatherBriefSection;
  cloud: WeatherBriefSection;
  precipitation: WeatherBriefSection;
  wind: WeatherBriefSection;
  official_bulletin: OfficialBulletinSection;
}

export interface SourceStatus {
  ifs: string;
  official_forecast: string;
  weather_bulletin: string;
  high_pv_backtest: string;
  recent_realization: string;
}

export interface SourceFreshnessItem {
  source: string;
  label: string;
  status: 'ok' | 'stale' | 'missing' | 'partial' | string;
  source_date: string | null;
  target_date: string | null;
  message: string | null;
  blocking_reason: string | null;
}

export interface Conflict {
  type: string;
  severity: string;
  message: string;
  impact: string;
}

export interface EvidenceCard {
  id: string;
  title: string;
  summary: string;
}

export interface Evidence {
  source_status: SourceStatus;
  conflicts: Conflict[];
  cards: EvidenceCard[];
}

export interface HistoryPlaybackSummary {
  days: number;
  direction_hit_rate: number | null;
  avg_mw_error: number | null;
  high_risk_days: number;
  high_risk_hit_rate: number | null;
}

export interface HistoryPlaybackRecord {
  date: string | null;
  replayed_action: string;
  official_midday_mw: number | null;
  recommended_realizable_mw: number | null;
  actual_midday_mw: number | null;
  mw_error: number | null;
  direction_result: string;
  cause_label: string | null;
}

export interface HighPvRealizationSummary {
  mode: string;
  sample_days: number;
  high_pv_days: number;
  avg_realization_ratio: number | null;
  weak_or_failed_rate: number | null;
  failed_rate: number | null;
  avg_shortfall_mw: number | null;
  p80_shortfall_mw: number | null;
  p10_realization_ratio: number | null;
  p50_realization_ratio: number | null;
  p90_realization_ratio: number | null;
  curtailment_suspected_days: number;
  business_interpretation: string;
  snapshot_note: string;
}

export interface HistoryPlayback {
  mode: string;
  mode_note: string;
  summary: HistoryPlaybackSummary;
  high_pv_realization_summary: HighPvRealizationSummary;
  records: HistoryPlaybackRecord[];
  snapshot_design: {
    table_name: string;
    status: string;
    migration_required: boolean;
  };
}

export interface DataQuality {
  backtest: Record<string, unknown>;
  source_status: SourceStatus;
}

export interface DailyTradingAdviceResponse {
  date: string | null;
  generated_at: string;
  model_name: string;
  data_freshness: string;
  source_freshness: SourceFreshnessItem[];
  advice: Advice;
  solar: SolarData;
  weather_brief: WeatherBrief;
  evidence: Evidence;
  history_playback: HistoryPlayback;
  data_quality: DataQuality;
  message: string | null;
}

// ---- hooks ----

export function useWeatherTradingAdvice(
  targetDate?: string,
  modelName = 'ecmwf_ifs',
  backtestDays = 60,
) {
  return useQuery({
    queryKey: ['weather', 'daily-trading-advice', targetDate, modelName, backtestDays],
    queryFn: async () => {
      const res = await apiClient.get<DailyTradingAdviceResponse>(
        '/weather/daily-trading-advice',
        { params: { target_date: targetDate, model_name: modelName, backtest_days: backtestDays } },
      );
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
